/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  EnhancementObject,
  ProcessingState,
} from "components/EnhancedSpeech/useEnhanceSpeech";
import { TrackItemProps } from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackItem";
import {
  CompletedEnhanceSpeechTrack,
  findNextCompletedEnhancement,
  isEnhanceSpeechCompletedSuccessfully,
} from "components/EnhancedSpeech/Utilities";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useAutoSelectTrack } from "components/EnhancedSpeech/ProTierEnhanceSpeech/useAutoSelectTrack";
import { TrackDetailProps } from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackDetail";
import { useEnhanceSpeechExports } from "components/EnhancedSpeech/ProTierEnhanceSpeech/useSelectableTrackItems/useEnhanceSpeechExports";
import { maybeApply } from "utilities/FunctionUtilities";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export const useSelectableTrackItems = (
  enhancements: EnhancementObject[]
): {
  trackItems: TrackItemProps[];
  trackDetail: TrackDetailProps | undefined;
} => {
  const [selectedTrack, setSelectedTrack] = useState<
    CompletedEnhanceSpeechTrack | undefined
  >();

  useAutoSelectTrack(enhancements, setSelectedTrack);

  const deleteAndUpdateSelection = useDeleteAndUpdateSelection(
    enhancements,
    selectedTrack,
    setSelectedTrack
  );

  const trackItems: TrackItemProps[] = useMemo(() => {
    return enhancements.map((enhancement): TrackItemProps => {
      return buildTrackItemProps(
        enhancement,
        selectedTrack,
        setSelectedTrack,
        deleteAndUpdateSelection
      );
    });
  }, [deleteAndUpdateSelection, enhancements, selectedTrack]);

  const clearSelection = useCallback(() => {
    setSelectedTrack(undefined);
  }, []);

  const { exportTrack, exporting } = useEnhanceSpeechExports();

  const trackDetail = useMemo((): TrackDetailProps | undefined => {
    if (!selectedTrack) {
      return undefined;
    } else {
      return {
        track: selectedTrack,
        onClose: clearSelection,
        isExporting: exporting.includes(selectedTrack.id),
        export: (
          enhanceStrength: number,
          backgroundStrength: number,
          enhancementEnabled: boolean
        ): void =>
          exportTrack(
            selectedTrack.id,
            enhanceStrength,
            backgroundStrength,
            enhancementEnabled,
            selectedTrack.modelVersion
          ),
      };
    }
  }, [clearSelection, exportTrack, exporting, selectedTrack]);

  return { trackItems, trackDetail };
};

const useDeleteAndUpdateSelection = (
  enhancements: EnhancementObject[],
  selectedTrack: CompletedEnhanceSpeechTrack | undefined,
  setSelectedTrack: Dispatch<
    SetStateAction<CompletedEnhanceSpeechTrack | undefined>
  >
): ((enhancement: EnhancementObject) => Promise<void>) =>
  useCallback(
    async (enhancement: EnhancementObject): Promise<void> => {
      const preDeleteEnhancements = enhancements;
      const deletedTrackId = enhancement.track.id;
      await enhancement.delete();

      if (selectedTrack?.id != enhancement.track.id) {
        return;
      }

      const nextCompletedEnhancement = findNextCompletedEnhancement(
        deletedTrackId,
        preDeleteEnhancements
      );

      setSelectedTrack(nextCompletedEnhancement);
    },
    [enhancements, selectedTrack?.id, setSelectedTrack]
  );

const buildTrackItemProps = (
  enhancement: EnhancementObject,
  selectedTrack: CompletedEnhanceSpeechTrack | undefined,
  setSelectedTrack: Dispatch<
    SetStateAction<CompletedEnhanceSpeechTrack | undefined>
  >,
  deleteAndUpdateSelection: (enhancement: EnhancementObject) => Promise<void>
): TrackItemProps => {
  return {
    statusProps: {
      duration: enhancement.track.duration,
      processingState: enhancement.processingState,
      errorType: enhancement.track.errorType,
      retryAtTime:
        maybeApply(
          enhancement.track.limits?.retryAtTime ?? undefined,
          (s) => new Date(s)
        ) ?? null,
    },
    id: enhancement.track.id,
    isUploading: enhancement.processingState === ProcessingState.uploading,
    uploadPercent: enhancement.percentUploaded ?? 0,
    isDeleting: enhancement.isDeleting,
    isFinished: enhancement.processingState === ProcessingState.finished,
    isProcessing: enhancement.processingState === ProcessingState.processing,
    isSelected: selectedTrack?.id === enhancement.track.id,
    onSelect: (): void => {
      if (isEnhanceSpeechCompletedSuccessfully(enhancement.track)) {
        setSelectedTrack(enhancement.track);
      }
    },
    remove: (): Promise<void> => deleteAndUpdateSelection(enhancement),
    trackName: enhancement.track.trackName,
    modelVersion: enhancement.track.modelVersion,
  };
};
